import React, { useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router-dom';
import { CategoryData, QueryData, PostData } from '../types/types';
import { GET_DEEP_CATEGORY_BY_SLUG, GET_POST_BY_SLUG } from '../graphql/categoriesQuery';
import CategoryDisplay from './CategoryDisplay';
import PostDisplay from './PostDisplay';
import usePathSegments from '../hooks/usePathSegments';
import useSEOData from '../hooks/useSEOData';

const CategoryPage: React.FC = () => {
  const location = useLocation();
  const { pathSegments, topLevelSlug } = usePathSegments();
  const isPostPath = pathSegments.includes('file');
  const postSlug = isPostPath ? pathSegments[pathSegments.length - 1] : null;

  const { data: catData } = useQuery<QueryData>(GET_DEEP_CATEGORY_BY_SLUG, {
    variables: { slug: topLevelSlug },
  });
  const { data: postData } = useQuery<{ post: PostData }>(GET_POST_BY_SLUG, {
    variables: { slug: postSlug },
    skip: !postSlug,
  });

  // Use the SEO data hook instead of inline logic
  const seoData = useSEOData(catData?.category, postData?.post);

  // ref to the scroll container
  const mainRef = useRef<HTMLDivElement>(null);

  // Track GA4 page views for deep navigation
  useEffect(() => {
    if (typeof window !== 'undefined' && window.gtag) {
      // Create a more descriptive page title based on the current view
      let pageTitle = 'Category';
      let customPath = location.pathname;

      if (isPostPath && postData?.post) {
        pageTitle = `Post: ${postData.post.title || postSlug}`;
      } else if (pathSegments.length > 1) {
        pageTitle = `Category: ${pathSegments.join(' > ')}`;
      }

      // Send a virtual page view
      window.gtag('event', 'page_view', {
        page_path: customPath,
        page_title: pageTitle,
        page_location: window.location.href,
        // Optional: add custom parameters to understand the navigation depth
        custom_parameters: {
          navigation_depth: pathSegments.length,
          is_post: isPostPath,
          category_path: pathSegments.join('/'),
        }
      });
    }
  }, [location.pathname, pathSegments, isPostPath, postData?.post, postSlug]);

  // whenever the pathSegments change (i.e. user clicked a new item),
  // if we're on mobile, scroll to the new column
  useEffect(() => {
    if (window.innerWidth <= 600 && mainRef.current) {
      const cols = mainRef.current.querySelectorAll<HTMLElement>('.column');
      const last = cols[cols.length - 1];
      if (last) {
        mainRef.current.scrollTo({
          left: last.offsetLeft,
          behavior: 'smooth',
        });
      }
    }
  }, [pathSegments]);

  // your recursive helper
  const findCategoryData = (segments: string[], category?: CategoryData): CategoryData | undefined => {
    if (segments.length <= 1 || !category) return category;
    const next = segments[1];
    const nextCat = category.children?.nodes.find(c => c.slug === next);
    return findCategoryData(segments.slice(1), nextCat);
  };

  return (
    <div
      id="finder__main"
      ref={mainRef}
      className="flex overflow-x-auto overflow-y-hidden"
    >
      {pathSegments.map((segment, i) => {
        if (segment === 'file' && postData?.post) {
          return <PostDisplay post={postData.post} key={i} />;
        } else if (pathSegments[i - 1] !== 'file') {
          const cat = findCategoryData(pathSegments.slice(0, i + 1), catData?.category);
          return (
            <div key={i} className={`flex-none column column-${i}`}>
              {cat && <CategoryDisplay category={cat} />}
            </div>
          );
        }
      })}
    </div>
  );
};

export default CategoryPage;